import { TextField } from "@mui/material";
import React from "react";
import { isValidFloorLoad } from "../../helper/HelperFunctions";
import {
  collectInvalidTotalErrorMessages,
  hasInvalidTotals,
  isGreaterThanMaxVolume,
  isGreaterThanMaxWeight,
  isInvalidPalletMax,
  isInvalidPalletMin,
  isInvalidPalletSpacesMax,
  isInvalidPalletSpacesMin,
  isInvalidVolume,
  isInvalidWeight,
  standardVolume,
} from "../../helper/ShipUnitUtilities";
import PoItemShipUnit from "../../types/PoItemShipUnit";
import { DropDown } from "../dropDown/DropDown";
import { FlagableTextField } from "./FlagableTextField";

interface IProps {
  poItemShipUnitsProps: PoItemShipUnit[];
  validationHandler: (hasValidationError: boolean) => void;
  onShipUnitsChange: (updatedUnits: PoItemShipUnit[]) => void;
  disableShipUnits: boolean;
}

export interface IState {
  poItemShipUnits: PoItemShipUnit[];
  originalPoItemShipUnits: PoItemShipUnit[];
  isPageEditable: boolean;
}

export default class OrderReleaseDetailsPoItemShipUnitPage extends React.Component<
  IProps,
  IState
> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      poItemShipUnits: props.poItemShipUnitsProps
        ? [...props.poItemShipUnitsProps]
        : [],
      originalPoItemShipUnits: props.poItemShipUnitsProps
        ? [...props.poItemShipUnitsProps]
        : [],
      isPageEditable: props.disableShipUnits,
    };
  }

  componentDidUpdate(prevProps: IProps) {
    if (prevProps.poItemShipUnitsProps !== this.props.poItemShipUnitsProps) {
      this.setState({
        isPageEditable: this.props.disableShipUnits,
        poItemShipUnits: [...this.props.poItemShipUnitsProps],
        originalPoItemShipUnits: [...this.props.poItemShipUnitsProps],
      });
    }
  }

  handleOnBlur(shipUnitId: string, event: any) {
    var rowsArray = this.state.poItemShipUnits;
    var newRow = rowsArray.map((row) => {
      if (row.shipUnitId === shipUnitId) {
        const targetName = event.target.name;

        if ("volume" === targetName) {
          row.volumeWithCommas = true;
        }
        if ("weight" === targetName) {
          row.weightWithCommans = true;
        }
      }
      return row;
    });
    this.setState({ poItemShipUnits: newRow });
  }

  handleOnFocus(shipUnitId: string, event: any) {
    var rowsArray = this.state.poItemShipUnits;
    var newRow = rowsArray.map((row) => {
      if (row.shipUnitId === shipUnitId) {
        const targetName = event.target.name;

        if ("volume" === targetName) {
          row.volumeWithCommas = false;
        }
        if ("weight" === targetName) {
          row.weightWithCommans = false;
        }
      }
      return row;
    });
    this.setState({ poItemShipUnits: newRow });
  }

  handleChange(shipUnitId: string, event: any) {
    const { name: targetName, value: targetValue } = event.target;

    const updatedRows = this.state.poItemShipUnits.map((unit) => {
      if (unit.shipUnitId === shipUnitId) {
        const updatedUnit = { ...unit };

        if ("loadMethod" === targetName) {
          if (updatedUnit.existingUnit) {
            updatedUnit.priorLoadMethod = updatedUnit.loadMethod;
          }
          updatedUnit.loadMethod = targetValue;

          // Reset logic for specific row
          updatedUnit.oversized = false;
          updatedUnit.pallet = 0;
          updatedUnit.palletSpaces = 0;
          updatedUnit.volume = 0;
          updatedUnit.weight = 0;
        }

        if ("volume" === targetName) {
          updatedUnit.volume = Number(targetValue);

          if (updatedUnit.loadMethod !== "PALLET") {
            const calculatedSpaces = Math.round(updatedUnit.volume / 110);

            updatedUnit.palletSpaces =
              calculatedSpaces < 1 ? 1 : calculatedSpaces;
          }
        }

        if ("weight" === targetName) {
          updatedUnit.weight = Number(targetValue);
        }

        if ("pallet" === targetName) {
          updatedUnit.pallet = Number(targetValue.replace(/[^0-9]/g, ""));
        }

        if ("palletSpaces" === targetName) {
          const val = targetValue.replace(/[^0-9]/g, "");
          updatedUnit.palletSpaces = Number(val);
          updatedUnit.volume = standardVolume(updatedUnit);
        }

        if ("oversize" === targetName) {
          updatedUnit.oversized = targetValue === "YES";
          if (!updatedUnit.oversized) {
            updatedUnit.volume = standardVolume(updatedUnit);
          }
        }

        return updatedUnit;
      }
      // Return other units untouched
      return unit;
    });

    this.setState({ poItemShipUnits: updatedRows }, () => {
      if (this.props.onShipUnitsChange) {
        this.props.onShipUnitsChange(this.state.poItemShipUnits);
      }
    });
  }

  handleValidation() {
    var hasErrors =
      this.state.poItemShipUnits?.some(
        (shipUnit: PoItemShipUnit) =>
          isInvalidPalletMin(shipUnit.loadMethod, shipUnit.pallet) ||
          isInvalidPalletSpacesMin(
            shipUnit.loadMethod,
            shipUnit.palletSpaces,
          ) ||
          isInvalidPalletMax(shipUnit.loadMethod, shipUnit.pallet) ||
          isInvalidPalletSpacesMax(
            shipUnit.loadMethod,
            shipUnit.palletSpaces,
          ) ||
          isInvalidVolume(shipUnit) ||
          isGreaterThanMaxVolume(shipUnit.volume) ||
          isInvalidWeight(shipUnit.weight) ||
          isGreaterThanMaxWeight(shipUnit.weight),
      ) || hasInvalidTotals(this.state.poItemShipUnits);
    this.props.validationHandler(hasErrors);
  }

  render() {
    this.handleValidation();
    return (
      <>
        <section className="sectionHeadingPadding">
          <div id="poItemShipUnitPage" className="container">
            <div
              id="poItemShipUnitsTable"
              style={{
                overflow: "auto",
                whiteSpace: "nowrap",
                paddingLeft: "75px",
              }}
            >
              <table className="table">
                <thead>
                  <tr>
                    <th>NMFC Class</th>
                    <th>Load Method</th>
                    <th>Pallets/SlipSheets</th>
                    <th>Pallets Spaces</th>
                    <th>Volume (cu ft)</th>
                    <th>Weight (lbs)</th>
                    <th>Cartons</th>
                    {this.state.isPageEditable ? null : (
                      <th>Oversized Pallets?</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {this.state?.poItemShipUnits?.map(
                    (shipUnit: PoItemShipUnit) => {
                      let rows: JSX.Element[];
                      if (!this.state.isPageEditable) {
                        rows = [
                          <tr>
                            <td>{shipUnit?.nmfcClass}</td>
                            <td>{shipUnit?.loadMethod}</td>
                            <td>{shipUnit?.pallet}</td>
                            <td>
                              {shipUnit.loadMethod === "PALLET"
                                ? shipUnit.palletSpaces
                                : 0}
                            </td>
                            <td>
                              {shipUnit.volumeWithCommas
                                ? Math.round(shipUnit.volume)
                                    .toString()
                                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                : Math.round(shipUnit.volume)
                                    .toString()
                                    .replace(/,/g, "")}
                            </td>
                            <td>
                              {shipUnit.weightWithCommans
                                ? Math.round(shipUnit.weight)
                                    .toString()
                                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                : Math.round(shipUnit.weight)
                                    .toString()
                                    .replace(/,/g, "")}
                            </td>
                            <td>
                              {Math.floor(shipUnit.cartons)
                                .toString()
                                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                            </td>
                            <td>{shipUnit?.oversized ? "Yes" : "No"}</td>
                          </tr>,
                        ];
                      } else {
                        rows = [
                          <tr>
                            <td>{shipUnit.nmfcClass}</td>
                            <td>
                              <DropDown
                                codes={["PALLET", "SLIP SHEETS", "FLOOR LOAD"]}
                                elementId={"loadMethod" + shipUnit.shipUnitId}
                                value={shipUnit.loadMethod}
                                onChange={this.handleChange.bind(
                                  this,
                                  shipUnit.shipUnitId,
                                )}
                                name="loadMethod"
                                disable={!this.state.isPageEditable}
                              />
                              <br />
                              {isValidFloorLoad(shipUnit.loadMethod) && (
                                <span
                                  id={"FloorLoad" + shipUnit.shipUnitId}
                                  style={{
                                    fontWeight: "bold",
                                    color: "black",
                                  }}
                                >
                                  Floor loaded shipments that <br />
                                  are less than truckload may <br />
                                  be requested to palletize
                                </span>
                              )}
                            </td>
                            <td>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "4px",
                                }}
                              >
                                <FlagableTextField
                                  id={"pallet" + shipUnit.shipUnitId}
                                  name="pallet"
                                  className="form-control"
                                  value={shipUnit.pallet}
                                  disabled={
                                    shipUnit.loadMethod === "FLOOR LOAD" ||
                                    !this.state.isPageEditable
                                  }
                                  onChange={this.handleChange.bind(
                                    this,
                                    shipUnit.shipUnitId,
                                  )}
                                />
                                {isInvalidPalletMin(
                                  shipUnit.loadMethod,
                                  shipUnit.pallet,
                                ) && (
                                  <span
                                    id={
                                      "palletSlipSheetsError" +
                                      shipUnit.shipUnitId
                                    }
                                    style={{
                                      fontWeight: "bold",
                                      color: "red",
                                    }}
                                  >
                                    Should be non-zero.
                                  </span>
                                )}
                              </div>
                              {isInvalidPalletMax(
                                shipUnit.loadMethod,
                                shipUnit.pallet,
                              ) && (
                                <span
                                  id={
                                    "palletSlipSheetsError" +
                                    shipUnit.shipUnitId
                                  }
                                  style={{
                                    fontWeight: "bold",
                                    color: "red",
                                  }}
                                >
                                  Should be less or equals to 90.
                                </span>
                              )}
                            </td>
                            <td>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "4px",
                                }}
                              >
                                <FlagableTextField
                                  id={"palletSpaces" + shipUnit.shipUnitId}
                                  name="palletSpaces"
                                  className="form-control"
                                  value={
                                    shipUnit.loadMethod === "PALLET"
                                      ? shipUnit.palletSpaces
                                      : 0
                                  }
                                  disabled={
                                    shipUnit.loadMethod === "FLOOR LOAD" ||
                                    shipUnit.loadMethod === "SLIP SHEETS" ||
                                    !this.state.isPageEditable
                                  }
                                  onChange={this.handleChange.bind(
                                    this,
                                    shipUnit.shipUnitId,
                                  )}
                                />
                                {isInvalidPalletSpacesMin(
                                  shipUnit.loadMethod,
                                  shipUnit.palletSpaces,
                                ) && (
                                  <span
                                    id={
                                      "palletSpacesError" + shipUnit.shipUnitId
                                    }
                                    style={{
                                      fontWeight: "bold",
                                      color: "red",
                                    }}
                                  >
                                    Should be non-zero.
                                  </span>
                                )}
                              </div>
                              {isInvalidPalletSpacesMax(
                                shipUnit.loadMethod,
                                shipUnit.palletSpaces,
                              ) && (
                                <span
                                  id={"palletSpacesError" + shipUnit.shipUnitId}
                                  style={{
                                    fontWeight: "bold",
                                    color: "red",
                                  }}
                                >
                                  Should be less or equals to 30.
                                </span>
                              )}
                            </td>
                            <td>
                              <TextField
                                id={"volume" + shipUnit.shipUnitId}
                                name="volume"
                                className="form-control"
                                value={
                                  shipUnit.volumeWithCommas
                                    ? Math.round(shipUnit.volume)
                                        .toString()
                                        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                    : Math.round(shipUnit.volume)
                                        .toString()
                                        .replace(/,/g, "")
                                }
                                onFocus={this.handleOnFocus.bind(
                                  this,
                                  shipUnit.shipUnitId,
                                )}
                                onBlur={this.handleOnBlur.bind(
                                  this,
                                  shipUnit.shipUnitId,
                                )}
                                onChange={this.handleChange.bind(
                                  this,
                                  shipUnit.shipUnitId,
                                )}
                                disabled={
                                  (shipUnit.loadMethod === "PALLET" &&
                                    !shipUnit.oversized) ||
                                  !this.state.isPageEditable
                                }
                              />
                              <br />
                              {shipUnit.volume <= standardVolume(shipUnit) &&
                                shipUnit.oversized && (
                                  <span
                                    id={"volumeError" + shipUnit.shipUnitId}
                                    style={{
                                      fontWeight: "bold",
                                      color: "red",
                                    }}
                                  >
                                    Please enter increased volume
                                  </span>
                                )}
                              {shipUnit.volume <= standardVolume(shipUnit) &&
                                shipUnit.oversized &&
                                shipUnit.volume === 0 && <br></br>}
                              {shipUnit.volume === 0 && (
                                <span
                                  id={"volumeError" + shipUnit.shipUnitId}
                                  style={{
                                    fontWeight: "bold",
                                    color: "red",
                                  }}
                                >
                                  Should be non-zero.
                                </span>
                              )}
                              {isGreaterThanMaxVolume(shipUnit.volume) && (
                                <span
                                  id={"volumeMaxError" + shipUnit.shipUnitId}
                                  style={{
                                    fontWeight: "bold",
                                    color: "red",
                                  }}
                                >
                                  Can not exceed
                                  <br /> 3,300 cu ft.
                                </span>
                              )}
                            </td>
                            <td>
                              <TextField
                                id={"weight" + shipUnit.shipUnitId}
                                name="weight"
                                className="form-control"
                                value={
                                  shipUnit.weightWithCommans
                                    ? Math.round(shipUnit.weight)
                                        .toString()
                                        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                    : Math.round(shipUnit.weight)
                                        .toString()
                                        .replace(/,/g, "")
                                }
                                onFocus={this.handleOnFocus.bind(
                                  this,
                                  shipUnit.shipUnitId,
                                )}
                                onBlur={this.handleOnBlur.bind(
                                  this,
                                  shipUnit.shipUnitId,
                                )}
                                onChange={this.handleChange.bind(
                                  this,
                                  shipUnit.shipUnitId,
                                )}
                                disabled={!this.state.isPageEditable}
                              />
                              <br />
                              {isInvalidWeight(shipUnit.weight) && (
                                <span
                                  id={"weightError" + shipUnit.shipUnitId}
                                  style={{
                                    fontWeight: "bold",
                                    color: "red",
                                  }}
                                >
                                  Should be non-zero.
                                </span>
                              )}
                              {isGreaterThanMaxWeight(shipUnit.weight) && (
                                <span
                                  id={"weightMaxError" + shipUnit.shipUnitId}
                                  style={{
                                    fontWeight: "bold",
                                    color: "red",
                                  }}
                                >
                                  Can not exceed
                                  <br /> 45,000 lbs.
                                </span>
                              )}
                            </td>
                            <td>
                              {Math.floor(shipUnit.cartons)
                                .toString()
                                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                            </td>
                          </tr>,
                        ];
                        if (shipUnit.loadMethod === "PALLET") {
                          rows.push(
                            <tr>
                              <td></td>
                              <td colSpan={5}>
                                <div className="d-flex justify-content-start align-items-center">
                                  <span
                                    style={{
                                      fontWeight: "bold",
                                      color: "black",
                                      paddingBottom: "10px",
                                    }}
                                  >
                                    For the weight field above, please enter the
                                    combined weight of your product and pallets
                                    (if palletized)
                                  </span>
                                  <div
                                    style={{
                                      width: "5em",
                                    }}
                                  >
                                    &nbsp;&nbsp;&nbsp;
                                  </div>
                                </div>
                                <div className="d-flex justify-content-start align-items-center">
                                  <span
                                    style={{
                                      fontWeight: "bold",
                                      color: "black",
                                    }}
                                  >
                                    Select "YES" only if you are shipping on
                                    larger than standard pallets (40” x 48”)
                                  </span>
                                  <div
                                    style={{
                                      width: "5em",
                                    }}
                                  >
                                    &nbsp;&nbsp;&nbsp;
                                    <DropDown
                                      codes={["YES", "NO"]}
                                      elementId={
                                        "oversize" + shipUnit.shipUnitId
                                      }
                                      value={shipUnit?.oversized ? "YES" : "NO"}
                                      onChange={this.handleChange.bind(
                                        this,
                                        shipUnit.shipUnitId,
                                      )}
                                      name="oversize"
                                    />
                                  </div>
                                </div>
                              </td>
                            </tr>,
                          );
                        }
                      }
                      return rows;
                    },
                  )}
                </tbody>
              </table>
              {collectInvalidTotalErrorMessages(
                this.state.poItemShipUnits,
              )?.map((invalidTotalErrorMessage: string) => (
                <span
                  id="totalErrors"
                  style={{
                    fontWeight: "bold",
                    color: "red",
                  }}
                >
                  {invalidTotalErrorMessage}
                  <br />
                </span>
              ))}
            </div>
          </div>
        </section>
      </>
    );
  }
}
